import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-all duration-150 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.98]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-accent text-bg-base shadow-sm',
          'hover:bg-accent-hover hover:shadow-[0_4px_16px_-4px_rgb(var(--c-accent)/0.4)]',
        ].join(' '),
        secondary: [
          'border border-border-default bg-bg-elevated/60 backdrop-blur-sm text-text-primary',
          'hover:border-accent/50 hover:text-text-primary hover:bg-bg-overlay',
        ].join(' '),
        ghost: 'text-text-secondary hover:text-text-primary hover:bg-bg-overlay',
        danger: 'bg-danger/90 text-white hover:bg-danger shadow-sm',
        outline: 'border border-accent/30 text-accent hover:bg-accent/10 hover:border-accent/50',
      },
      size: {
        sm: 'h-9 touch:h-11 px-3 text-xs rounded-md',
        md: 'h-10 touch:h-11 px-4 py-2',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9 touch:h-11 touch:w-11 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = 'Button'
